import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import deepmerge from "deepmerge";

let _isLoaded = false;
const _eventQueue = [];

const setBookingSearchBox = (
	{ from, to, isHotel = false, force = false } = {},
	priority = 100,
	trigger = false
) => {
	if (!trigger) {
		const settings = {
			packages: {},
			flights: {},
			cruises: {},
		};

		if (from) {
			settings.packages.selectedFrom = {
				value: from,
				force,
			};
			settings.flights.selectedFrom = {
				value: from,
				force,
			};
			settings.cruises.selectedFrom = {
				value: from,
				force,
			};
		}

		if (to) {
			settings.packages.selectedTo = {
				value: to,
				isHotel,
				force,
			};
		}

		_eventQueue.push({ settings, priority });
	}

	if (!_isLoaded && !trigger) {
		return false;
	}

	const _prioritizedQueue = _eventQueue
		.sort((a, b) => a.priority - b.priority)
		.map(event => event.settings);

	if (_prioritizedQueue.length) {
		const settings = deepmerge.all(_prioritizedQueue);
		window.WEB_CDN_BSB.overrideDefaults(settings);
		_eventQueue.length = 0;
	}

	return true;
};

const SearchBox = ({
	type,
	language,
	mode,
	alias,
	setIsLoading,
	setUpdateType,
	isNewMobileDesign,
}) => {
	const bsbRef = useRef();
	let shadowDomObserver = null;
	let widgetObserver = null;

	const setupWidgetObserver = _ref => {
		/* This observer synchronizes the parent data-type attribute in order to enable CSS targeting for layout shift */
		const target = _ref;

		// Create an observer instance
		widgetObserver = new MutationObserver(mutations => {
			mutations.forEach(mutation => {
				if (mutation.type === "attributes" && mutation.attributeName === "data-type") {
					// Check if the mutation is for the attribute name "data-type" and sends the value to the parent
					setUpdateType(mutation.target.dataset?.type);
				}
			});
		});

		// Configuration of the observer
		const observerConfig = { attributes: true };

		if (target) {
			// Tell user agent to start observing
			widgetObserver.observe(target, observerConfig);
		}
	};

	const setupWidgetShadowDomObserver = _ref => {
		// Note: This is a temporary work-around for an existing bug where resizing the widget resets the selected tab to "packages"
		const bsbNodes = _ref?.shadowRoot.querySelectorAll(".booking-search-box-app");

		if (bsbNodes) {
			// Select the latest target node
			const target = bsbNodes[bsbNodes.length - 1];

			// Create an observer instance
			shadowDomObserver = new MutationObserver(mutations => {
				mutations.forEach(mutation => {
					if (mutation.type === "childList") {
						const tablist = mutation.target.querySelector("nav[role='tablist']");
						if (tablist) {
							const selectedTab = Array.from(tablist.children).find(
								tab => tab.getAttribute("aria-selected") === "true"
							);
							if (selectedTab) {
								const tabProduct = selectedTab.getAttribute("aria-controls")?.toLowerCase();
								if (tabProduct) {
									setUpdateType(tabProduct);
								}
							}
						}
					}
				});
			});

			// Configuration of the observer
			const observerConfig = { childList: true };

			if (target) {
				// Tell user agent to start observing
				shadowDomObserver.observe(target, observerConfig);
			}
		}
	};

	useEffect(() => {
		const bsbScript = document.createElement("script");
		bsbScript.src = process.env.GATSBY_WEB_CDN_BSB;
		bsbScript.async = true;
		bsbScript.defer = true;
		bsbScript.onload = () => {
			const preferredGatewayValue = JSON.parse(localStorage.getItem("preferredGateway"))?.value;
			window.WEB_CDN_BSB.onLoad(() => {
				setBookingSearchBox(
					{
						from: preferredGatewayValue,
					},
					10
				);
				_isLoaded = true;
				setIsLoading(false);
				setBookingSearchBox({}, null, true);

				// Setup the observer for the widget
				setupWidgetObserver(bsbRef.current);
				// Setup the observer for the shadow DOM
				setupWidgetShadowDomObserver(bsbRef.current);

				if (window?.Genesys) {
					window.Genesys("subscribe", "Launcher.ready", () => {
						if (window.WEB_CDN_BSB?.subscribe) {
							window.WEB_CDN_BSB.subscribe("widgetToggle", isOpen => {
								const bodyTag = document.getElementsByTagName("body");
								const chatBot = document.getElementById("genesys-messenger");
								if (isOpen) {
									chatBot?.classList.add("bsbOpen");
									bodyTag?.[0]?.setAttribute("data-scroll-locked", "");
								} else {
									chatBot?.classList.remove("bsbOpen");
									bodyTag?.[0]?.removeAttribute("data-scroll-locked");
								}
							});
						}
					});
				}
			});
		};

		document.body.appendChild(bsbScript);
		return () => {
			if (bsbScript) {
				_isLoaded = false; // Reset the flag
				setIsLoading(true);
				if (window.WEB_CDN_BSB && window.WEB_CDN_BSB.destroy) {
					window.WEB_CDN_BSB.destroy();

					if (widgetObserver !== null) {
						// Stop observing the target node
						widgetObserver.disconnect();
					}
					if (shadowDomObserver !== null) {
						// Stop observing the target node
						shadowDomObserver.disconnect();
					}
				}
				bsbScript?.remove();

				const chatBot = document.getElementById("genesys-messenger");
				if (chatBot) {
					chatBot.classList.remove("bsbOpen");
				}
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Determine the search type to display

	return (
		<div
			ref={bsbRef}
			id="booking-search-box-wrapper"
			data-lang={language}
			data-alias={alias}
			data-type={type}
			data-mode={mode}
			data-brand="sov"
			data-new-mobile-design={isNewMobileDesign}
		/>
	);
};

SearchBox.propTypes = {
	type: PropTypes.oneOf(["packages", "flights", "hotels", "cruises"]),
	language: PropTypes.oneOf(["en", "fr"]),
	mode: PropTypes.oneOf(["search", "tabs"]),
	alias: PropTypes.string,
	setIsLoading: PropTypes.func,
	setUpdateType: PropTypes.func,
	isNewMobileDesign: PropTypes.bool,
};

SearchBox.defaultProps = {
	type: "packages",
	language: "en",
	mode: "search",
	alias: "drv",
	setIsLoading: () => {},
	setUpdateType: () => {},
	isNewMobileDesign: false,
};

export default SearchBox;
export { SearchBox };
